import * as RadixDialog from '@radix-ui/react-dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import React, { useMemo } from 'react'
import styled, { css, keyframes } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { useTranslation } from '@root/shared/localization'

import type { ClassNameProps } from '@root/shared/react'

export type GlassDialogIntent = 'success' | 'error'
export type GlassDialogProps = {
  intent?: GlassDialogIntent | AsyncStateN.AsyncStateN<unknown, unknown>
} & React.PropsWithChildren &
  RadixDialog.DialogProps &
  ClassNameProps

export const GlassDialog: React.FC<GlassDialogProps> = ({
  children,
  intent,
  className,
  ...props
}) => {
  const { t } = useTranslation()

  const resolvedIntent = useMemo(() => {
    if (!intent) {
      return intent
    }
    if (typeof intent === 'string') {
      return intent
    }
    if (AsyncStateN.isSuccess(intent)) {
      return 'success'
    }
    if (AsyncStateN.isError(intent)) {
      return 'error'
    }
  }, [intent])

  return (
    <Root modal {...props}>
      <VisuallyHidden.Root>
        <RadixDialog.DialogTitle>
          {t('common.attention')}
        </RadixDialog.DialogTitle>
        <RadixDialog.Description>
          {t('common.attention')}
        </RadixDialog.Description>
      </VisuallyHidden.Root>
      <RadixDialog.Portal>
        <Overlay />
        <Content className={className} $intent={resolvedIntent}>
          {children}
        </Content>
      </RadixDialog.Portal>
    </Root>
  )
}
const overlayShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`

const Root = styled(RadixDialog.Dialog)``

const Overlay = styled(RadixDialog.Overlay)`
  z-index: 100;
  background-color: rgba(0 0 0 / 0.6);
  position: fixed;
  inset: 0;
  animation: ${overlayShow} 150ms linear forwards;
`

const Content = styled(RadixDialog.Content)<{
  $intent: GlassDialogIntent | undefined
}>`
  z-index: 100;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.07),
    0 0 4px 2px var(--appearanceA-1);

  border-radius: 20px;
  backdrop-filter: blur(30px);
  overflow: hidden;

  animation: ${contentShow} 150ms linear;

  ${(props) => {
    switch (props.$intent) {
      case 'error': {
        return css`
          background:
            radial-gradient(
              54.75% 54.75% at 50% 0%,
              rgba(255, 17, 60, 0.2) 0%,
              rgba(255, 17, 60, 0) 100%
            ),
            rgba(255, 255, 255, 0.07);
        `
      }
      case 'success': {
        return css`
          background:
            radial-gradient(
              54.75% 54.75% at 50% 0%,
              rgba(0, 255, 56, 0.16) 0%,
              rgba(0, 255, 56, 0) 100%
            ),
            rgba(255, 255, 255, 0.07);
        `
      }
      default: {
        return css`
          background: rgba(255, 255, 255, 0.07);
        `
      }
    }
  }}
`
